import React, { useEffect, useState } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { DayPilot, DayPilotScheduler } from '@daypilot/daypilot-lite-react';
import { Skeleton, Empty } from 'antd';
import { useDispatch, useSelector } from 'react-redux';

import {
    selectFilteredEventsForYearView,
    selectYearViewDetail,
    selectYearViewSelectedVehicleId,
    selectYearViewLoadingStatus,
    setPopupVisibility,
    fetchYearViewData
} from '../../../store/serviceCalendarSlice';

export const ServiceCalendarPopupYearView = () => {
    const dispatch = useDispatch();
    const vehicleDetail = useSelector(selectYearViewDetail);
    const vehicleId = useSelector(selectYearViewSelectedVehicleId);
    const vehicleEvents = useSelector(selectFilteredEventsForYearView);
    const yearViewLoadingStatus = useSelector(selectYearViewLoadingStatus);

    const currentYear = new Date().getFullYear();

    // Fetch year view data when component mounts or vehicleId changes
    useEffect(() => {
        if (vehicleId) {
            dispatch(fetchYearViewData({ vehicleId, year: currentYear }));
        }
    }, [dispatch, vehicleId, currentYear]);

    const handleClose = (e) => {
        e.stopPropagation();
        dispatch(
            setPopupVisibility({
                popupName: 'yearView',
                isVisible: false
            })
        );
    };

    const [config, setConfig] = useState({
        days: 31,
        scale: 'Day',
        timeHeaders: [{ groupBy: 'Year' }, { groupBy: 'Day', format: 'd' }],
        cellWidthSpec: 'Auto',
        heightSpec: 'Max',
        height: 500,
        cellWidthMin: 40,
        startDate: DayPilot.Date.today().firstDayOfYear(),
        resources: [],
        events: [],
        onBeforeCellRender: (args) => {
            args.cell.properties.fontColor = '#dddddd';
            args.cell.properties.verticalAlignment = 'center';
            args.cell.properties.horizontalAlignment = 'center';
            // IMPORTANT!! replace start with resource YYYY-MM but keep day from original start , tricky way for lite version as start and end properties of resources not working so the event always mathcing with January ( Yui make note in 2025)
            let datetsart = args.cell.resource.toString('yyyy-MM') + args.cell.start.toString('-dd');
            datetsart = datetsart + 'T00:00:00';
            datetsart = new DayPilot.Date(datetsart);
            args.cell.start = datetsart;
            let dateend = args.cell.resource.toString('yyyy-MM') + args.cell.end.toString('-dd');
            dateend = new DayPilot.Date(dateend);
            args.cell.end = dateend + 'T00:00:00';
            args.cell.properties.text = args.cell.start.toString('ddd');
        }
    });

    useEffect(() => {
        // Only process events if we have data and it's not loading
        if (yearViewLoadingStatus === 'loading' || !vehicleEvents?.length) {
            setConfig((prevConfig) => ({
                ...prevConfig,
                resources: [],
                events: []
            }));
            return;
        }

        const resources = [];
        const startDate = DayPilot.Date.today().firstDayOfYear();
        for (let i = 0; i < 12; i++) {
            const month = startDate.addMonths(i);
            resources.push({
                name: month.toString('MMMM'),
                start: month.toString('yyyy-MM-01T00:00:00'),
                end: month.addMonths(1).toString('yyyy-MM-01T00:00:00'),
                id: month.toString('yyyy-MM')
            });
        }
        // IMPORTANT!! change to for loop to handle tricky way for lite version start and end properties of resources not working so the event always mathcing with January ( Yui make note in 2025)
        const mappedEvents = [];
        for (let i = 0; i < vehicleEvents.length; i++) {
            const event = vehicleEvents[i];
            // if start and end in different month, need to create multiple events for each month
            const startEvent = new DayPilot.Date(event.start);
            const endEvent = new DayPilot.Date(event.end);
            //conpare month difference
            const startMonth = startEvent.getMonth();
            const endMonth = endEvent.getMonth();
            if (startMonth !== endMonth) {
                // create event for each month
                let currentStart = startEvent;
                let monthFake = '01';
                while (currentStart.getMonth() !== endEvent.getMonth()) {
                    const monthEnd = currentStart.lastDayOfMonth().addDays(1);
                    // if start is the last day of the moth than 01 chnage to 02
                    monthFake = '02';

                    mappedEvents.push({
                        start: currentStart.toString('yyyy-') + '01-' + currentStart.toString('dd') + 'T00:00:00',
                        end: monthEnd.toString('yyyy-') + monthFake + '-' + monthEnd.toString('dd') + 'T00:00:00',
                        id: event.id + '-' + currentStart.toString('MM'),
                        backColor: event.backColor || '#6aa84f',
                        resource: currentStart.toString('yyyy-MM')
                    });
                    currentStart = monthEnd;
                }
                // add the last event
                monthFake = '01';
                if (endEvent.getDay() === endEvent.firstDayOfMonth().getDay()) {
                    monthFake = '02';
                } // issue if event is last day of month
                mappedEvents.push({
                    start: currentStart.toString('yyyy-') + '01-' + currentStart.toString('dd') + 'T00:00:00',
                    end: new DayPilot.Date(endEvent).toString('yyyy-') + monthFake + '-' + new DayPilot.Date(endEvent).toString('dd') + 'T00:00:00',
                    id: event.id + '-' + currentStart.toString('MM'),
                    backColor: event.backColor || '#6aa84f',
                    resource: currentStart.toString('yyyy-MM')
                });
                continue;
            } else {
                let monthFake = '01';
                if (startEvent.getDay() === startEvent.lastDayOfMonth().getDay()) {
                    monthFake = '02';
                } // issue if event is last day of month
                mappedEvents.push({
                    start: new DayPilot.Date(event.start).toString('yyyy-') + '01-' + new DayPilot.Date(event.start).toString('dd') + 'T00:00:00',
                    end: new DayPilot.Date(event.end).toString('yyyy-') + monthFake + '-' + new DayPilot.Date(event.end).toString('dd') + 'T00:00:00',
                    id: event.id,
                    backColor: event.backColor || '#6aa84f',
                    resource: new DayPilot.Date(event.start).toString('yyyy-MM')
                });
            }
        }
        setConfig((prevConfig) => ({
            ...prevConfig,
            resources: resources,
            events: mappedEvents
        }));
    }, [vehicleEvents, yearViewLoadingStatus]);

    return (
        <div className="yearView-main-div-overlay">
            <div className="yearView-main-div">
                <div className="yearView-title w-full flex items-center justify-between relative">
                    <div className="absolute left-1/2 transform -translate-x-1/2 font-semibold">Year View - {vehicleDetail}</div>
                    <CloseOutlined onClick={handleClose} className="ml-auto hover:bg-gray-500 p-1 rounded cursor-pointer" />
                </div>
                <div className="year-view-body">
                    <div style={{ textAlign: 'center' }}>
                        {yearViewLoadingStatus === 'loading' ? (
                            <div style={{ padding: '20px' }}>
                                <Skeleton active paragraph={{ rows: 8 }} />
                            </div>
                        ) : vehicleEvents?.length === 0 ? (
                            <div style={{ padding: '40px' }}>
                                <Empty
                                    description={`No services found for ${vehicleDetail} in ${currentYear}`}
                                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                                />
                            </div>
                        ) : (
                            <DayPilotScheduler {...config} />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
